/** NUMELE CAMERELOR ȘI COTELE unui nivel de arhitectură, scrise ca pe tronson.
 *
 *  Cerut de Stefan (23.09.2026): pe planul de arhitectură camera se scrie cu
 *  numele ei, «la fel cum e scris și la tronsoane», fără cifra în cerc și fără
 *  cheia de legendă de alături; iar cotele «cum sunt în tronsoane — integrate,
 *  inteligente, fără duplicate, aliniate».
 *
 *  Nu e o a doua implementare: nivelul se împachetează ca document de tronson
 *  exact cum îl deschide masa (`nivelTronson.nivelLaTronson`, în cadrul
 *  clădirii), se derivă planul (`tronson/doc.deriva`), iar numele și cotele ies
 *  din aceleași funcții ca pe tronson (`Desen.etichetelePlanului`,
 *  `cote.cotelePlanului`). Ce se învață acolo se vede și aici.
 *
 *  Planul tronsonului e în cadrul CLĂDIRII — rotit cu unghiul ei dominant —,
 *  deci grupul se întoarce în cadrul desenului cu aceeași transformare ca
 *  `nivelTronson.dinLocal`: `R(teta)`, apoi originea. Scara nu se schimbă, deci
 *  mărirea pânzei e și a planului.
 */
import { memo, useMemo } from "react";
import { northingFirst, type CanonicalDoc } from "../canonical";
import { deriva, type PlanTronson } from "../tronson/doc";
import { etichetelePlanului, NumeleCamerelor } from "../tronson/Desen";
import { cotelePlanului, type Cutie } from "../tronson/cote";
import { Cote } from "../tronson/CoteDesen";
import { useT } from "../../i18n";
import { cadrulCladirii, nivelLaTronson, type Cadru } from "./nivelTronson";

/** Planul de tronson al nivelului, în cadrul clădirii lui. `null` când nivelul
 *  nu se poate împacheta — atunci planul rămâne fără nume și fără cote, nu
 *  cade tot desenul. */
export function planulNivelului(doc: CanonicalDoc, storeyId: string):
    { plan: PlanTronson; cadru: Cadru } | null {
  try {
    const oglindit = !northingFirst(doc.crs as { epsg?: number } | null);
    const cadru = cadrulCladirii(doc, storeyId, oglindit);
    return { plan: deriva(nivelLaTronson(doc, storeyId, cadru).initial), cadru };
  } catch {
    return null;
  }
}

/** Sub atâția pixeli pe metru cotele tac. Cifra cotei are corp fix pe ecran,
 *  iar la depărtarea la care se vede tot ansamblul golul dintre două corpuri
 *  (~10 m) are câțiva pixeli: gabaritul unui corp se scria peste vecinul lui.
 *  Pe tronson problema nu există — e un singur corp. */
const COTE_MIN_PX_PE_M = 3;

function AdnotariNivel({
  doc, storeyId, pxPerM, etichete, cote, tus, hartie,
}: {
  /** Documentul în cadrul DESENULUI (`northUp` + originea locală). */
  doc: CanonicalDoc;
  storeyId: string;
  pxPerM: number;
  /** Se scriu numele și ariile camerelor? (stratul «Etichete») */
  etichete: boolean;
  /** Se scriu cotele? (stratul «Cote») */
  cote: boolean;
  /** Tușul, când nu e cel negru al tronsonului — pe planșa de noapte. */
  tus?: string;
  /** Hârtia de sub cifrele care nu încap în linia lor — pe noapte, neagră. */
  hartie?: string;
}) {
  // Limba: numele vin din `numeleFelului`, care citește limba curentă.
  useT();
  const nivel = useMemo(() => planulNivelului(doc, storeyId), [doc, storeyId]);
  const scrise = useMemo(
    () => (nivel && etichete ? etichetelePlanului(nivel.plan, pxPerM) : []),
    [nivel, etichete, pxPerM]);
  const cotele = useMemo(() => {
    if (!nivel || !cote || !(pxPerM >= COTE_MIN_PX_PE_M)) return [];
    const ocupate: Cutie[] = scrise.flatMap((e) => (e ? [e.cutie] : []));
    return cotelePlanului(nivel.plan, pxPerM, ocupate);
  }, [nivel, cote, pxPerM, scrise]);
  if (!nivel) return null;
  const { o, teta } = nivel.cadru;
  return (
    <g pointerEvents="none"
       transform={`translate(${o[0]} ${o[1]}) rotate(${(teta * 180) / Math.PI})`}>
      <NumeleCamerelor etichete={scrise} tus={tus} />
      {cotele.length > 0 && <Cote cote={cotele} k={pxPerM} culoare={tus}
                                     hartie={hartie} />}
    </g>
  );
}

/** MEMOIZAT: pânza se re-randează la fiecare cadru de panoramare și zoom, iar
 *  proprietățile de aici nu se schimbă între cadre (scara vine așezată, v.
 *  `useScaraDetaliului`). Fără `memo` desenul se reconcilia de la capăt de
 *  60 de ori pe secundă. */
export default memo(AdnotariNivel);
